import React, { useState, useEffect, useRef } from "react";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
} from "chart.js";
import { Pie, Line } from "react-chartjs-2";
import * as THREE from "three";
import "./loan-calculator.css";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale
);

const LoanCalculator = () => {
  const [loanAmount, setLoanAmount] = useState(5000);
  const [interestRate, setInterestRate] = useState(0.5);
  const [loanTerm, setLoanTerm] = useState(90);
  const [emi, setEmi] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);
  const [chartData, setChartData] = useState([]);
  const threeRef = useRef(null);

  const calculateEMI = (P, R, days) => {
    const months = days / 30;
    const r = R / 12 / 100;
    const emiVal =
      (P * r * Math.pow(1 + r, months)) /
      (Math.pow(1 + r, months) - 1);

    const totalPay = emiVal * months;
    const interest = totalPay - P;

    setEmi(emiVal.toFixed(2));
    setTotalInterest(interest.toFixed(2));

    let balance = P;
    const dataPoints = [];

    for (let i = 1; i <= months; i++) {
      const interestPart = balance * r;
      const principalPart = emiVal - interestPart;
      balance -= principalPart;
      dataPoints.push(Math.max(balance, 0));
    }

    setChartData(dataPoints);
  };

  useEffect(() => {
    calculateEMI(loanAmount, interestRate, loanTerm);
  }, [loanAmount, interestRate, loanTerm]);

  const pieData = {
    labels: ["Principal", "Interest"],
    datasets: [
      {
        data: [loanAmount, totalInterest],
        backgroundColor: ["#00ffd5", "#d50ac6"],
        borderWidth: 0,
      },
    ],
  };

  const lineData = {
    labels: chartData.map((_, i) => i + 1),
    datasets: [
      {
        label: "Outstanding Balance",
        data: chartData,
        borderColor: "#00ffd5",
        backgroundColor: "rgba(213,10,198,0.2)",
        fill: true,
        tension: 0.4,
        pointRadius: 0,
      },
    ],
  };

  const lineOptions = {
    plugins: { legend: { display: false } },
    scales: {
      x: { display: false },
      y: { ticks: { color: "#000" }, grid: { color: "rgba(0,0,0,0.05)" } },
    },
  };

  useEffect(() => {
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    const renderer = new THREE.WebGLRenderer({ alpha: true });
    renderer.setSize(window.innerWidth, window.innerHeight);

    if (threeRef.current) {
      threeRef.current.appendChild(renderer.domElement);
    }

    const particlesCount = 300;
    const geometry = new THREE.BufferGeometry();
    const positions = [];

    for (let i = 0; i < particlesCount; i++) {
      positions.push(
        (Math.random() - 0.5) * 50,
        (Math.random() - 0.5) * 50,
        (Math.random() - 0.5) * 50
      );
    }
    geometry.setAttribute(
      "position",
      new THREE.Float32BufferAttribute(positions, 3)
    );

    const material = new THREE.PointsMaterial({
      color: 0xd50ac6,
      size: 0.2,
      transparent: true,
      opacity: 0.7,
    });

    const particles = new THREE.Points(geometry, material);
    scene.add(particles);

    camera.position.z = 30;

    const animate = () => {
      particles.rotation.y += 0.0015;
      particles.rotation.x += 0.0005;
      renderer.render(scene, camera);
      requestAnimationFrame(animate);
    };

    animate();

    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (threeRef.current && renderer.domElement.parentNode) {
        threeRef.current.removeChild(renderer.domElement);
      }
      renderer.dispose();
      geometry.dispose();
      material.dispose();
    };
  }, []);

  return (
    <div className="loan-calc-full">
      <div ref={threeRef} className="three-bg"></div>

      <div className="calc-content">
        <div className="calc-left">
          <h1 className="calc-heading">
            Smart <span> Loan </span>Calculator
          </h1>
          <p className="calc-sub">
           Move the sliders to instantly visualize EMI, interest impact, and overall repayment costs.
          </p>

          <div className="sliders">
            <div className="slider-box">
              <label>Loan Amount: ₹{loanAmount.toLocaleString()}</label>
              <input
                type="range"
                min="5000"
                max="200000"
                step="5000"
                value={loanAmount}
                onChange={(e) => setLoanAmount(parseInt(e.target.value))}
              />
            </div>

            <div className="slider-box">
              <label>Interest Rate: {interestRate}%</label>
              <input
                type="range"
                min="0.5"
                max="30"
                step="0.1"
                value={interestRate}
                onChange={(e) =>
                  setInterestRate(parseFloat(e.target.value))
                }
              />
            </div>

            <div className="slider-box">
              <label>Loan Tenure: {loanTerm} Days</label>
              <input
                type="range"
                min="90"
                max="365"
                step="1"
                value={loanTerm}
                onChange={(e) => setLoanTerm(parseInt(e.target.value))}
              />
            </div>
          </div>

          <div className="calc-results">
            <div className="result-box">
              <h4>Monthly EMI</h4>
              <p>₹{emi}</p>
            </div>
            <div className="result-box">
              <h4>Total Interest</h4>
              <p>₹{totalInterest}</p>
            </div>
            <div className="result-box">
              <h4>Total Payment</h4>
              <p>
                ₹{" "}
                {(
                  parseFloat(emi) *
                  (loanTerm / 30)
                ).toLocaleString(undefined, {
                  maximumFractionDigits: 2,
                })}
              </p>
            </div>
          </div>
        </div>

        <div className="calc-right">
          <div className="chart-section">
            <div className="chart-card">
              <Pie data={pieData} />
            </div>
            <div className="line-card">
              <Line data={lineData} options={lineOptions} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoanCalculator;
